import React, { useState, useEffect, useMemo } from 'react';
import { Check, CircleDot, Film } from 'lucide-react';
import { PlatformMark } from '../PlatformMark';
import { useParams } from 'react-router-dom';
import './styles.css';
import MediaUploader from '../MediaUploader/MediaUploader';

import { getChannelList, GetPostUploadURL, GetThumbnailUploadURL, GetPostThumbnailURL, StartPost, UpdatePost, GetPost, UploadFile } from '../../Client/ManagementServiceClient';

import { getAuth } from "firebase/auth";
import { isPublishingAvailable } from '../SocialMedia/channelAvailability';

// Per-platform presentation and the copy limits we warn against. Limits are the
// public caps for each network; the composer enforces the tightest one across
// the channels actually selected.
const PLATFORM_META = {
    Youtube: { label: 'YouTube', titleMax: 100, descriptionMax: 5000 },
    TikTok: { label: 'TikTok', titleMax: 150, descriptionMax: 2200 },
    Instagram: { label: 'Instagram', titleMax: 125, descriptionMax: 2200 },
    Facebook: { label: 'Facebook', titleMax: 255, descriptionMax: 5000 },
    Test: { label: 'Test', titleMax: 200, descriptionMax: 5000 },
};

const DEFAULT_LIMITS = { titleMax: 150, descriptionMax: 5000 };

const platformMeta = (channelType) => PLATFORM_META[channelType] || {
    label: channelType || 'Channel',
   
    ...DEFAULT_LIMITS,
};

const NET_TICKS_AT_UNIX_EPOCH = 621355968000000000;

const formatDatePart = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatTimePart = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const formatDateTimeLocal = (date) => `${formatDatePart(date)}T${formatTimePart(date)}`;

// The API models these as pydantic datetimes, so they have to go out as ISO
// 8601. A bare int is read as a Unix timestamp there, which is why .NET ticks
// were being rejected.
const toIsoUtc = (dateTime) => {
    const date = dateTime ? new Date(dateTime) : new Date();
    if (Number.isNaN(date.getTime())) {
        return new Date().toISOString();
    }
    return date.toISOString();
};

// Reads a timestamp coming back from the API — ISO today, .NET ticks in older
// documents — and returns it as ISO, or '' if it is neither.
const apiDateToIso = (value) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const ticks = Number(value);
    if (Number.isFinite(ticks) && ticks > NET_TICKS_AT_UNIX_EPOCH) {
        return new Date(Math.floor((ticks - NET_TICKS_AT_UNIX_EPOCH) / 10000)).toISOString();
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

const normalizePostTimeToLocalInput = (postAtTimeValue) => {
    if (postAtTimeValue === null || postAtTimeValue === undefined || postAtTimeValue === '') {
        return '';
    }

    // Backend stores PostAtTimeUTC in .NET ticks.
    const ticks = Number(postAtTimeValue);
    if (Number.isFinite(ticks) && ticks > NET_TICKS_AT_UNIX_EPOCH) {
        const unixMs = Math.floor((ticks - NET_TICKS_AT_UNIX_EPOCH) / 10000);
        const date = new Date(unixMs);
        if (!Number.isNaN(date.getTime())) {
            return formatDateTimeLocal(date);
        }
    }

    // Fallback: try ISO/Date-compatible string.
    const parsedDate = new Date(postAtTimeValue);
    if (!Number.isNaN(parsedDate.getTime())) {
        return formatDateTimeLocal(parsedDate);
    }

    return '';
};

const parseTagInput = (raw) => String(raw || '')
    .split(/[\s,]+/)
    .map(tag => tag.trim().replace(/^#+/, ''))
    .filter(Boolean);

const describeScheduleTime = (localValue) => {
    const date = new Date(localValue);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

// Quick picks so the common schedules don't require driving the date input.
const schedulePresets = () => {
    const inAnHour = new Date(Date.now() + 60 * 60 * 1000);

    const tonight = new Date();
    tonight.setHours(19, 0, 0, 0);
    if (tonight.getTime() <= Date.now()) {
        tonight.setDate(tonight.getDate() + 1);
    }

    const tomorrowMorning = new Date();
    tomorrowMorning.setDate(tomorrowMorning.getDate() + 1);
    tomorrowMorning.setHours(9, 0, 0, 0);

    return [
        { label: 'In an hour', value: formatDateTimeLocal(inAnHour) },
        { label: '7:00 PM', value: formatDateTimeLocal(tonight) },
        { label: 'Tomorrow 9 AM', value: formatDateTimeLocal(tomorrowMorning) },
    ];
};

// `onPublished` lets the caller (the project flow) treat publishing as the one
// action for this step, instead of a save button plus a separate finalize.
function VideoUploader({ forcedPostId = null, initialData = null, hideHeader = false, onPublished = null, aiSuggested = false }) {
    const { postId: urlPostId } = useParams(); // Get postId from URL parameters
    const effectivePostId = forcedPostId || urlPostId || null;
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [thumbnail, setThumbnail] = useState({ file: null, url: '' });
    const [video, setVideo] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [tags, setTags] = useState([]);
    const [tagDraft, setTagDraft] = useState('');
    /* The creator's own answer to the AI-disclosure question, or null for "not
       answered". Null is not the same as false: unanswered lets the backend
       fall back to what the project looks like, which is what makes the
       suggestion work at all. Answering false is how you overrule it. */
    const [aiGenerated, setAiGenerated] = useState(null);
    const [scheduleMode, setScheduleMode] = useState('now');
    const [uploadDateTime, setUploadDateTime] = useState('');
    const [thumbnailUploading, setThumbnailUploading] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    const [thumbnailUploaded, setThumbnailUploaded] = useState(false);
    const [videoUploaded, setVideoUploaded] = useState(false);
    const [channels, setChannels] = useState([]);
    const [channelsLoading, setChannelsLoading] = useState(true);
    const [selectedChannelIds, setSelectedChannelIds] = useState(new Set());
    const [postId, setPostId] = useState(effectivePostId); // Prefer forced postId, then URL
    const [isSaving, setIsSaving] = useState(false);
    const [status, setStatus] = useState(null); // { type: 'success' | 'error', message }
    const [createdAtIso, setCreatedAtIso] = useState('');
    const [showVideoPicker, setShowVideoPicker] = useState(false);
    const [showThumbnailPicker, setShowThumbnailPicker] = useState(false);

    useEffect(() => {
        const fetchChannels = async () => {
            const auth = getAuth();
            const user = auth.currentUser;
            try {
                const idToken = await user.getIdToken();
                const { status: channelStatus, myJson } = await getChannelList(idToken);
                if (channelStatus !== 200) {
                    console.error('Failed to fetch channel list:', channelStatus, myJson);
                    return;
                }
                // A channel that cannot be published to is not offered as a target,
                // even if the account was linked before it was held back.
                setChannels((Array.isArray(myJson) ? myJson : []).filter(
                    channel => isPublishingAvailable(channel.ChannelType)
                ));
            } catch (error) {
                console.error('Error fetching channel list:', error);
            } finally {
                setChannelsLoading(false);
            }
        };

        const fetchPostId = async () => {
            // Only fetch a new postId if one wasn't provided via prop/URL
            if (effectivePostId) {
                const auth = getAuth();
                const user = auth.currentUser;
                try {
                    const idToken = await user.getIdToken();
                    const { status: postStatus, myJson } = await GetPost(idToken, effectivePostId);
                    if (postStatus !== 200) {
                        console.error('Failed to fetch post:', postStatus, myJson);
                        return;
                    }
                    setCreatedAtIso(apiDateToIso(myJson.CreatedDateTime));
                    if (typeof myJson.Title === 'string' && myJson.Title.trim()) {
                        setTitle((prev) => (String(prev || '').trim() ? prev : myJson.Title));
                    }
                    if (typeof myJson.Description === 'string' && myJson.Description.trim()) {
                        setDescription((prev) => (String(prev || '').trim() ? prev : myJson.Description));
                    }
                    if (typeof myJson.AIGenerated === 'boolean') {
                        setAiGenerated(myJson.AIGenerated);
                    }
                    if (Array.isArray(myJson.Tags) && myJson.Tags.length > 0) {
                        setTags((prev) => (prev.length ? prev : parseTagInput(myJson.Tags.join(' '))));
                    }
                    const normalizedPostTime = normalizePostTimeToLocalInput(myJson.PostAtTimeUTC);
                    if (normalizedPostTime) {
                        setUploadDateTime((prev) => (String(prev || '').trim() ? prev : normalizedPostTime));
                        // A time still in the future means this post is scheduled;
                        // a past time is just the stamp from when it was saved.
                        if (new Date(normalizedPostTime).getTime() > Date.now()) {
                            setScheduleMode('later');
                        }
                    }
                    if (typeof myJson.VideoURL === 'string' && myJson.VideoURL.trim()) {
                        setVideoUrl((prev) => (String(prev || '').trim() ? prev : myJson.VideoURL));
                    } else if (typeof myJson.VideoUrl === 'string' && myJson.VideoUrl.trim()) {
                        setVideoUrl((prev) => (String(prev || '').trim() ? prev : myJson.VideoUrl));
                    }

                    try {
                        const thumbResult = await GetPostThumbnailURL(idToken, effectivePostId);
                        if (thumbResult.status === 200 && thumbResult.myJson?.upload_url) {
                            setThumbnail((prev) => ({ ...prev, file: null, url: thumbResult.myJson.upload_url }));
                            setThumbnailUploaded(true);
                        }
                    } catch (thumbnailError) {
                        console.warn('Unable to fetch thumbnail URL for post:', thumbnailError);
                    }

                    // Keyed by ChannelId so this no longer depends on the channel
                    // list having loaded first (it is fetched in parallel).
                    const savedChannelIds = (myJson.userChannels || [])
                        .map(channel => channel.ChannelId)
                        .filter(Boolean);
                    if (savedChannelIds.length > 0) {
                        setSelectedChannelIds(new Set(savedChannelIds));
                    }
                } catch (error) {
                    console.error('Error fetching post:', error);
                }
                return;
            }

            const auth = getAuth();
            const user = auth.currentUser;
            try {
                const idToken = await user.getIdToken();
                const { status: startStatus, myJson } = await StartPost(idToken);
                if (startStatus !== 200) {
                    console.error('Failed to start post:', startStatus, myJson);
                    return;
                }
                setPostId(myJson.PostId);
            } catch (error) {
                console.error('Error starting post:', error);
            }
        };

        fetchPostId();
        fetchChannels();
        // eslint-disable-next-line
    }, [effectivePostId]);

    useEffect(() => {
        if (!initialData) {
            return;
        }

        if (typeof initialData.title === 'string' && initialData.title.trim()) {
            setTitle(initialData.title.trim());
        }

        if (typeof initialData.description === 'string' && initialData.description.trim()) {
            setDescription(initialData.description.trim());
        }

        if (Array.isArray(initialData.tags) && initialData.tags.length > 0) {
            const normalized = parseTagInput(initialData.tags.join(' '));
            if (normalized.length) {
                setTags(normalized);
            }
        }

        if (typeof initialData.videoUrl === 'string' && initialData.videoUrl.trim()) {
            setVideoUrl(initialData.videoUrl.trim());
        }
    }, [initialData]);

    // Upload files when postId becomes available
    useEffect(() => {
        if (postId) {
            if (thumbnail.file && !thumbnailUploaded && !thumbnailUploading) {
                uploadThumbnailFile(thumbnail.file);
            }
            if (video && !videoUploaded && !videoUploading) {
                uploadVideoFile(video);
            }
        }
        // eslint-disable-next-line
    }, [postId]);

    // Computed once per mount so the selected preset stays highlighted instead
    // of drifting away as the clock ticks past it.
    const presets = useMemo(() => schedulePresets(), []);

    const filePreviewUrl = useMemo(() => (video ? URL.createObjectURL(video) : ''), [video]);
    useEffect(() => () => {
        if (filePreviewUrl) {
            URL.revokeObjectURL(filePreviewUrl);
        }
    }, [filePreviewUrl]);

    const previewVideoSrc = filePreviewUrl || videoUrl;
    // In the project flow the cut arrives from the Edit step, so the video is a
    // result to confirm rather than a field to fill in.
    const videoCameFromEdit = Boolean(String(initialData?.videoUrl || '').trim());

    const selectedChannels = useMemo(
        () => channels.filter(channel => selectedChannelIds.has(channel.ChannelId)),
        [channels, selectedChannelIds]
    );

    // Tightest caps across the chosen platforms; the generic defaults until one
    // is chosen, so the counters never disappear.
    const limits = useMemo(() => {
        if (selectedChannels.length === 0) {
            return DEFAULT_LIMITS;
        }
        return selectedChannels.reduce((acc, channel) => {
            const meta = platformMeta(channel.ChannelType);
            return {
                titleMax: Math.min(acc.titleMax, meta.titleMax),
                descriptionMax: Math.min(acc.descriptionMax, meta.descriptionMax),
            };
        }, { titleMax: Infinity, descriptionMax: Infinity });
    }, [selectedChannels]);

    const isUploadingMedia = thumbnailUploading || videoUploading;

    // Everything standing between the draft and a publishable post, stated as
    // the thing to go do.
    const blockers = useMemo(() => {
        const issues = [];
        if (!title.trim()) issues.push('Add a title.');
        if (!video && !videoUrl) issues.push('Add a video — upload a file or paste a URL.');
        if (selectedChannelIds.size === 0) issues.push('Pick at least one channel to post to.');
        if (scheduleMode === 'later' && !uploadDateTime) issues.push('Choose a date and time to publish.');
        if (title.length > limits.titleMax) issues.push(`Title is ${title.length - limits.titleMax} characters over the limit.`);
        if (description.length > limits.descriptionMax) issues.push(`Description is ${description.length - limits.descriptionMax} characters over the limit.`);
        return issues;
    }, [title, description, video, videoUrl, selectedChannelIds, scheduleMode, uploadDateTime, limits]);

    const toggleChannel = (channelId) => {
        setSelectedChannelIds((prev) => {
            const next = new Set(prev);
            if (next.has(channelId)) {
                next.delete(channelId);
            } else {
                next.add(channelId);
            }
            return next;
        });
        setStatus(null);
    };

    const toggleAllChannels = () => {
        setSelectedChannelIds((prev) => (
            prev.size === channels.length ? new Set() : new Set(channels.map(channel => channel.ChannelId))
        ));
        setStatus(null);
    };

    const commitTagDraft = () => {
        const parsed = parseTagInput(tagDraft);
        if (parsed.length === 0) {
            setTagDraft('');
            return;
        }
        setTags(prev => Array.from(new Set([...prev, ...parsed])));
        setTagDraft('');
    };

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            e.preventDefault();
            commitTagDraft();
            return;
        }
        if (e.key === 'Backspace' && !tagDraft && tags.length > 0) {
            setTags(prev => prev.slice(0, -1));
        }
    };

    const removeTag = (tag) => setTags(prev => prev.filter(item => item !== tag));

    const uploadVideoFile = async (file) => {
        if (!postId) {
            console.error('No postId available for video upload');
            return;
        }

        setVideoUploading(true);
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            const idToken = await user.getIdToken();

            const { status: urlStatus, myJson } = await GetPostUploadURL(idToken, postId);
            if (urlStatus !== 200) {
                console.error('Failed to get video upload URL:', urlStatus, myJson);
                setStatus({ type: 'error', message: 'Could not start the video upload. Try again.' });
                return;
            }

            await UploadFile(myJson.upload_url, file);
            setVideoUploaded(true);
        } catch (error) {
            console.error('Error uploading video:', error);
            setStatus({ type: 'error', message: 'The video upload failed. Try again.' });
        } finally {
            setVideoUploading(false);
        }
    };

    const uploadThumbnailFile = async (file) => {
        if (!postId) {
            console.error('No postId available for thumbnail upload');
            return;
        }

        setThumbnailUploading(true);
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            const idToken = await user.getIdToken();

            const { status: urlStatus, myJson } = await GetThumbnailUploadURL(idToken, postId);
            if (urlStatus !== 200) {
                console.error('Failed to get thumbnail upload URL:', urlStatus, myJson);
                setStatus({ type: 'error', message: 'Could not start the thumbnail upload. Try again.' });
                return;
            }

            await UploadFile(myJson.upload_url, file);
            setThumbnailUploaded(true);
        } catch (error) {
            console.error('Error uploading thumbnail:', error);
            setStatus({ type: 'error', message: 'The thumbnail upload failed. Try again.' });
        } finally {
            setThumbnailUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus(null);

        if (isUploadingMedia) {
            setStatus({ type: 'error', message: 'Hold on — media is still uploading.' });
            return;
        }

        if (blockers.length > 0) {
            setStatus({ type: 'error', message: blockers[0] });
            return;
        }

        setIsSaving(true);
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            const idToken = await user.getIdToken();

            const nowIso = toIsoUtc(new Date());
            const pendingTags = Array.from(new Set([...tags, ...parseTagInput(tagDraft)]));

            // Post data matches the Post.py interface.
            const postData = {
                PostId: postId,
                Title: title,
                Description: description,
                Tags: pendingTags,
                VideoURL: videoUrl || '',
                VideoUrl: videoUrl || '',
                PostAtTimeUTC: scheduleMode === 'later' && uploadDateTime
                    ? toIsoUtc(uploadDateTime)
                    : nowIso,
                userChannels: selectedChannels.map(channel => ({
                    ChannelId: channel.ChannelId,
                    ChannelName: channel.ChannelName,
                    ChannelType: channel.ChannelType
                })),
                State: "Pending",
                // Sent as-is, null included. Null means the creator did not
                // answer, and the backend resolves it from the project at
                // publish time -- so a post saved with the suggestion showing
                // publishes with the suggestion applied.
                AIGenerated: aiGenerated,
                UserId: user.uid,
                // Keep the post's original creation stamp rather than stamping
                // "now" over it on every save.
                CreatedDateTime: createdAtIso || nowIso,
                ModifiedDateTime: nowIso
            };

            const updateResponse = await UpdatePost(idToken, postData);
            if (updateResponse.status !== 200) {
                console.error('Failed to update post:', updateResponse.status, updateResponse.myJson);
                setStatus({ type: 'error', message: 'Could not save the post. Please try again.' });
                return;
            }

            setTags(pendingTags);
            setTagDraft('');
            const destination = selectedChannels.length === 1
                ? selectedChannels[0].ChannelName
                : `${selectedChannels.length} channels`;
            setStatus({
                type: 'success',
                message: scheduleMode === 'later'
                    ? `Scheduled for ${describeScheduleTime(uploadDateTime)} on ${destination}.`
                    : `Queued to publish now on ${destination}.`,
            });

            if (onPublished) {
                await onPublished();
            }
        } catch (error) {
            console.error('Error saving post:', error);
            setStatus({ type: 'error', message: 'Something went wrong while saving. Please try again.' });
        } finally {
            setIsSaving(false);
        }
    };

    const submitLabel = () => {
        if (isUploadingMedia) return 'Uploading media…';
        if (isSaving) return 'Saving…';
        return scheduleMode === 'later' ? 'Schedule post' : 'Publish now';
    };

    return (
        <div className="post-composer">
            {!hideHeader && (
                <div className="post-composer-header">
                    <h1>{effectivePostId ? 'Edit post' : 'New post'}</h1>
                    <p>Write the copy, pick where it goes, and choose when it publishes.</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="post-composer-layout">
                <div className="post-composer-main">
                    <section className="pc-card">
                        <div className="pc-card-head">
                            <h2>Details</h2>
                            <span className="pc-card-hint">What people see with your video</span>
                        </div>

                        <div className="post-field">
                            <div className="post-field-label">
                                <label htmlFor="title">Title</label>
                                <span className={`post-counter ${title.length > limits.titleMax ? 'over' : ''}`}>
                                    {title.length}/{limits.titleMax}
                                </span>
                            </div>
                            <input
                                type="text"
                                id="title"
                                value={title}
                                placeholder="Give the post a headline"
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </div>

                        <div className="post-field">
                            <div className="post-field-label">
                                <label htmlFor="description">Description</label>
                                <span className={`post-counter ${description.length > limits.descriptionMax ? 'over' : ''}`}>
                                    {description.length}/{limits.descriptionMax}
                                </span>
                            </div>
                            <textarea
                                id="description"
                                value={description}
                                placeholder="Say what the video is about. The first line or two is what most viewers read."
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>

                        <div className="post-field">
                            <div className="post-field-label">
                                <label htmlFor="hashtags">Hashtags</label>
                                <span className="post-field-hint">Enter or comma to add</span>
                            </div>
                            <div className="post-tag-input" onClick={() => document.getElementById('hashtags')?.focus()}>
                                {tags.map(tag => (
                                    <span className="post-tag" key={tag}>
                                        #{tag}
                                        <button
                                            type="button"
                                            className="post-tag-remove"
                                            onClick={() => removeTag(tag)}
                                            aria-label={`Remove #${tag}`}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                                <input
                                    type="text"
                                    id="hashtags"
                                    value={tagDraft}
                                    placeholder={tags.length ? 'Add another' : 'e.g. shorts editing viral'}
                                    onChange={(e) => setTagDraft(e.target.value)}
                                    onKeyDown={handleTagKeyDown}
                                    onBlur={commitTagDraft}
                                />
                            </div>
                        </div>
                        {/* Platforms require realistic AI-generated video to be
                            disclosed, and the penalty for missing it lands on the
                            creator's channel, not on us. So this is suggested for
                            projects that generated video and left switchable
                            either way -- we can see what a project generated, but
                            not whether any of it survived the edit. */}
                        <div className="post-field">
                            <label className="post-ai-toggle">
                                <input
                                    type="checkbox"
                                    checked={aiGenerated === null ? aiSuggested : aiGenerated}
                                    onChange={(e) => setAiGenerated(e.target.checked)}
                                />
                                <span className="post-ai-toggle-text">
                                    <strong>This video is AI-generated</strong>
                                    <span className="post-field-hint">
                                        {aiGenerated === null && aiSuggested
                                            ? 'Suggested because this project generated video. Adds the required label on YouTube and Instagram.'
                                            : 'Adds the required label on YouTube and Instagram. Leave off for ordinary edits of real footage.'}
                                    </span>
                                </span>
                            </label>
                        </div>
                    </section>

                    <section className="pc-card">
                        <div className="pc-card-head">
                            <h2>Media</h2>
                            <span className="pc-card-hint">The video and its cover image</span>
                        </div>

                        {videoCameFromEdit && !showVideoPicker ? (
                            <div className="post-media-row">
                                <div className="post-media-row-text">
                                    <strong>Video from your edit</strong>
                                    <span>The finished cut carries over automatically.</span>
                                </div>
                                <button type="button" className="post-link-btn" onClick={() => setShowVideoPicker(true)}>
                                    Use a different video
                                </button>
                            </div>
                        ) : (
                            <MediaUploader
                                label="Video"
                                type="video"
                                contentUrl={videoUrl}
                                contentFile={video}
                                isUploading={videoUploading}
                                isUploaded={videoUploaded || (videoUrl && !video)}
                                onUrlChange={(url) => {
                                    setVideoUrl(url);
                                    setVideo(null);
                                }}
                                onFileChange={(file) => {
                                    if (file) {
                                        setVideoUrl('');
                                        setVideo(file);
                                        if (postId) {
                                            uploadVideoFile(file);
                                        }
                                    } else {
                                        setVideo(null);
                                    }
                                }}
                                uploadMode="url"
                            />
                        )}

                        {thumbnail.url || thumbnail.file || showThumbnailPicker ? (
                            <MediaUploader
                                label="Thumbnail"
                                type="image"
                                contentUrl={thumbnail?.url || ''}
                                contentFile={thumbnail?.file || null}
                                isUploading={thumbnailUploading}
                                isUploaded={thumbnailUploaded || (thumbnail?.url && !thumbnail?.file)}
                                onUrlChange={(url) => setThumbnail({ url, file: null })}
                                onFileChange={(file) => {
                                    if (file) {
                                        setThumbnail({ file, url: '' });
                                        if (postId) {
                                            uploadThumbnailFile(file);
                                        }
                                    } else {
                                        setThumbnail({ file: null, url: '' });
                                    }
                                }}
                                uploadMode="file"
                            />
                        ) : (
                            <div className="post-media-row">
                                <div className="post-media-row-text">
                                    <strong>Thumbnail <span className="post-optional">optional</span></strong>
                                    <span>Platforms pick a frame from the video if you skip this.</span>
                                </div>
                                <button type="button" className="post-link-btn" onClick={() => setShowThumbnailPicker(true)}>
                                    Add thumbnail
                                </button>
                            </div>
                        )}
                    </section>

                    <section className="pc-card">
                        <div className="pc-card-head">
                            <h2>Where to post</h2>
                            {channels.length > 0 && (
                                <button type="button" className="post-link-btn" onClick={toggleAllChannels}>
                                    {selectedChannelIds.size === channels.length ? 'Clear all' : 'Select all'}
                                </button>
                            )}
                        </div>

                        {channelsLoading ? (
                            <p className="post-empty">Loading your channels…</p>
                        ) : channels.length === 0 ? (
                            <p className="post-empty">
                                No channels connected yet. Link a YouTube, Instagram, or Facebook
                                account from Social Media settings, then come back here.
                            </p>
                        ) : (
                            <div className="post-channel-grid">
                                {channels.map((channel) => {
                                    const meta = platformMeta(channel.ChannelType);
                                    const isSelected = selectedChannelIds.has(channel.ChannelId);
                                    return (
                                        <button
                                            type="button"
                                            key={channel.ChannelId}
                                            className={`post-channel ${isSelected ? 'selected' : ''}`}
                                            onClick={() => toggleChannel(channel.ChannelId)}
                                            aria-pressed={isSelected}
                                        >
                                            <span className={`post-channel-mark platform-${String(channel.ChannelType || '').toLowerCase()}`}>
                                                <PlatformMark platform={channel.ChannelType} size={14} /> {!['Youtube', 'Instagram', 'Facebook', 'TikTok'].includes(channel.ChannelType) && <CircleDot size={14} aria-hidden="true" />}
                                            </span>
                                            <span className="post-channel-text">
                                                <span className="post-channel-name">{channel.ChannelName}</span>
                                                <span className="post-channel-type">{meta.label}</span>
                                            </span>
                                            <span className="post-channel-check" aria-hidden="true">{isSelected ? <Check size={14} aria-hidden="true" /> : null}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    <section className="pc-card">
                        <div className="pc-card-head">
                            <h2>When to post</h2>
                        </div>

                        <div className="post-segmented" role="group" aria-label="Publish timing">
                            <button
                                type="button"
                                className={scheduleMode === 'now' ? 'active' : ''}
                                aria-pressed={scheduleMode === 'now'}
                                onClick={() => setScheduleMode('now')}
                            >
                                Publish now
                            </button>
                            <button
                                type="button"
                                className={scheduleMode === 'later' ? 'active' : ''}
                                aria-pressed={scheduleMode === 'later'}
                                onClick={() => {
                                    setScheduleMode('later');
                                    if (!uploadDateTime) {
                                        setUploadDateTime(presets[0].value);
                                    }
                                }}
                            >
                                Schedule
                            </button>
                        </div>

                        {scheduleMode === 'later' ? (
                            <div className="post-schedule">
                                <input
                                    type="datetime-local"
                                    id="uploadDateTime"
                                    value={uploadDateTime}
                                    min={formatDateTimeLocal(new Date())}
                                    onChange={(e) => setUploadDateTime(e.target.value)}
                                />
                                <div className="post-schedule-presets">
                                    {presets.map(preset => (
                                        <button
                                            type="button"
                                            key={preset.label}
                                            className={`post-chip ${uploadDateTime === preset.value ? 'active' : ''}`}
                                            onClick={() => setUploadDateTime(preset.value)}
                                        >
                                            {preset.label}
                                        </button>
                                    ))}
                                </div>
                                {uploadDateTime && (
                                    <p className="post-schedule-summary">
                                        Publishes {describeScheduleTime(uploadDateTime)} — your local time.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p className="post-schedule-summary">Goes out as soon as you hit publish.</p>
                        )}
                    </section>
                </div>

                <aside className="post-composer-side">
                    <div className="pc-card post-preview-card">
                        <div className="pc-card-head">
                            <h2>Preview</h2>
                        </div>
                        <div className="post-preview-frame">
                            {previewVideoSrc ? (
                                <video src={previewVideoSrc} controls preload="metadata" poster={thumbnail?.url || undefined} />
                            ) : thumbnail?.url ? (
                                <img src={thumbnail.url} alt="Post thumbnail" />
                            ) : (
                                <div className="post-preview-placeholder">
                                    <Film size={28} aria-hidden="true" />
                                    <p>Your video shows up here</p>
                                </div>
                            )}
                        </div>
                        <div className="post-preview-copy">
                            <h3>{title.trim() || 'Untitled post'}</h3>
                            {description.trim() && <p>{description}</p>}
                            {tags.length > 0 && (
                                <p className="post-preview-tags">{tags.map(tag => `#${tag}`).join(' ')}</p>
                            )}
                        </div>
                        <div className="post-preview-meta">
                            {selectedChannels.length > 0 ? (
                                <span>{selectedChannels.map(channel => channel.ChannelName).join(', ')}</span>
                            ) : (
                                <span className="muted">No channel selected</span>
                            )}
                            <span className="muted">
                                {scheduleMode === 'later' && uploadDateTime
                                    ? describeScheduleTime(uploadDateTime)
                                    : 'Immediately'}
                            </span>
                        </div>
                    </div>

                    <div className="pc-card post-submit-card">
                        {blockers.length > 0 && (
                            <ul className="post-blockers">
                                {blockers.map(issue => <li key={issue}>{issue}</li>)}
                            </ul>
                        )}

                        {status && (
                            <div className={`pc-status ${status.type}`} role="status">
                                {status.message}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="post-submit"
                            disabled={isUploadingMedia || isSaving || blockers.length > 0}
                        >
                            {submitLabel()}
                        </button>

                        {onPublished && (
                            <p className="post-submit-note">
                                This also completes the Post step and opens the report.
                            </p>
                        )}
                    </div>
                </aside>
            </form>
        </div>
    );
}

export { VideoUploader };
